import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEventHandler, useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { updateDriver } from '../../../apis/queries';
import SubmitButton from '../../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../../helpers/validation';
import { Driver } from '../../../types';
import DisplayFieldErrors from '../../../utils/DisplayFormError';
import { formatDateTime } from '../../../utils/formatDateTime';
import { notification, NotificationKinds } from '../../../widgets/Notifications/notifications';

interface UpdateDriverFormInput {
  name: string;
  phoneNumber: string;
  nickName: string;
  isActive: boolean;
}

const DriverDetailForm = ({
  driver,
  isEditMode,
  onEditFormSubmit
}: {
  driver: Driver;
  isEditMode: boolean;
  isEditFormSubmitting: boolean;
  onEditFormSubmit: (value: boolean) => void;
}) => {
  const { name, phoneNumber, isActive, _id, nickName, createdAt, updatedAt } = driver;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (driver: UpdateDriverFormInput) => {
      return updateDriver(String(_id), {
        name: driver.name,
        phoneNumber: driver.phoneNumber,
        nickName: driver.nickName,
        isActive: driver.isActive
      });
    },
    onSuccess: data => {
      const { data: updatedDriver } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `DRIVER UPDATED`,
        subtitle: `${updatedDriver?.name} has been updated`
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers', { id: _id }] });
    }
  });

  const loading = mutation?.isPending;

  const createdBy = driver.createdBy.fullName ?? '';

  useEffect(() => {
    onEditFormSubmit(loading);
  }, [loading]);

  const methods = useForm<UpdateDriverFormInput>({
    mode: 'onBlur',
    defaultValues: {
      name,
      phoneNumber,
      nickName,
      isActive
    }
  });

  const {
    register,
    formState: { isValid, errors, isDirty },
    reset,
    getValues
  } = methods;

  const onUpdateDriver: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    const fields = getValues();
    mutation?.mutate(fields);
  };

  const disabled = loading || !isEditMode;

  const handleCancel = () => {
    reset();
  };

  return (
    <Form>
      <Form.Group className="my-4">
        <Form.Check
          className="fw-bold"
          type="switch"
          label={'Activate Driver'}
          {...register('isActive', { disabled })}
        />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Driver Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="John Doe"
          {...register('name', {
            required: 'Name is required',
            validate: value => value.trim() !== '' || 'Full name cannot be empty',
            pattern: {
              value: /^[a-zA-Z]+( [a-zA-Z]+)*$/,
              message: 'Full name should contain only alphabets'
            },
            disabled,
            minLength: { value: 3, message: 'Name must be at least 3 characters' }
          })}
          onInput={e => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
          }}
        />
        {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="text"
          placeholder="08039283847"
          {...register('phoneNumber', {
            required: 'Phone number is required',
            pattern: {
              value: /^[0-9]+$/,
              message: 'Phone number must contain only digits'
            },
            validate: {
              isNotEmpty: value => value.trim() !== '' || 'Phone number cannot be empty',
              isValidFormat: isValidPhoneNumber
            },
            disabled
          })}
          onInput={e => {
            e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
          }}
        />
        {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Nickname</Form.Label>
        <Form.Control
          type="text"
          placeholder="johndoe"
          {...register('nickName', {
            disabled,
            minLength: { value: 3, message: 'Nickname must be atleast 3 characters' },
            validate: value => value.trim() !== '' || 'Nickname cannot be empty',
            pattern: {
              value: /^[a-zA-Z]+$/,
              message: 'Nickname should contain only alphabets'
            }
          })}
          onInput={e => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
          }}
        />
        {errors?.nickName && <DisplayFieldErrors displayText={errors.nickName.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created By</Form.Label>
        <Form.Control type="text" placeholder="Created By" disabled={true} value={createdBy.toString()} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created At</Form.Label>
        <Form.Control type="text" placeholder="Created At" disabled={true} value={formatDateTime(createdAt)} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Last Updated</Form.Label>
        <Form.Control type="text" placeholder="Updated At" disabled={true} value={formatDateTime(updatedAt)} />
      </Form.Group>

      {isEditMode && (
        <Row className="my-5">
          <Col>
            <SubmitButton
              type="button"
              buttonText="cancel"
              variant="white"
              onClick={handleCancel}
              disabled={disabled}
              loading={false}
            />
          </Col>
          <Col>
            <SubmitButton
              type="submit"
              buttonText="Submit"
              onClick={onUpdateDriver}
              loading={loading}
              disabled={!isDirty || !isValid || disabled}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default DriverDetailForm;
